import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './EventsPage.css';

const ACCENT_COLORS = [
  '#6c8fff', '#e87461', '#4caf82', '#f58220',
  '#a78bfa', '#34d399', '#f472b6', '#60a5fa',
];

function EventCard({ event, onClick }) {
  return (
    <div className="event-card" onClick={() => onClick(event)} role="button" tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(event)}>
      <div className="event-card__color-bar" style={{ background: event.coverColor }} />
      <div className="event-card__body">
        <h3 className="event-card__name">{event.name}</h3>
        {event.description && (
          <p className="event-card__desc">{event.description}</p>
        )}
        <span className="event-card__count">{event.mediaCount} file{event.mediaCount !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
}

function CreateEventModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [coverColor, setCoverColor] = useState(ACCENT_COLORS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return setError('Event name is required');
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/api/events', { name: name.trim(), description, coverColor });
      onCreate(data.event);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <h2>New event</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="banner banner-error">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <label className="modal-label">
            Event name *
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Wedding Day, Product Launch"
              maxLength={200}
              autoFocus
              required
            />
          </label>

          <label className="modal-label">
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </label>

          <div className="modal-label">
            Accent color
            <div className="color-picker">
              {ACCENT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`color-swatch ${coverColor === c ? 'color-swatch--active' : ''}`}
                  style={{ background: c }}
                  onClick={() => setCoverColor(c)}
                  aria-label={c}
                />
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Create event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function EventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.get('/api/events');
      setEvents(data.events);
    } catch {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  function handleCreated(event) {
    setEvents((prev) => [event, ...prev]);
  }

  if (loading) return <div className="events-loading"><div className="spinner" /></div>;

  return (
    <div className="events-page page-wrapper">
      <div className="events-header">
        <h1>Events</h1>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + New event
        </button>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {events.length === 0 ? (
        <div className="events-empty">
          <span className="events-empty__icon">📁</span>
          <p>No events yet</p>
          <p className="events-empty__sub">Create an event to start organising your photos and videos.</p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            Create your first event
          </button>
        </div>
      ) : (
        <div className="events-grid">
          {events.map((ev) => (
            <EventCard key={ev.id} event={ev} onClick={() => navigate(`/events/${ev.id}`)} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateEventModal onClose={() => setShowCreate(false)} onCreate={handleCreated} />
      )}
    </div>
  );
}
